import React, { useState, useEffect, useCallback } from 'react';
import { studentService } from '../api/studentService';
import { attendanceService } from '../api/attendanceService';
import toast from 'react-hot-toast';

const Attendance = () => {
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await studentService.getClasses();
      setClasses(response.classes || []);
      if (response.classes && response.classes.length > 0) {
        setSelectedClass(response.classes[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch classes');
      console.error('Error fetching classes:', error);
    }
  }, []);

  const fetchStudentsByClass = useCallback(async () => {
    try {
      setLoading(true);
      const response = await studentService.getStudentsByClass(selectedClass);
      const studentsList = response.students || [];
      setStudents(studentsList);
      
      // Initialize attendance state for new students
      const newAttendance = {};
      studentsList.forEach(student => {
        if (!attendance[student._id]) {
          newAttendance[student._id] = 'present';
        }
      });
      setAttendance(prev => ({ ...prev, ...newAttendance }));
    } catch (error) {
      toast.error('Failed to fetch students');
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, attendance]);

  const fetchAttendanceForDate = useCallback(async () => {
    try {
      const response = await attendanceService.getAttendanceByDate(selectedDate);
      const attendanceData = response.attendance || [];
      
      // Update attendance state with existing data
      const existingAttendance = {};
      attendanceData.forEach(record => {
        if (record.studentId) {
          existingAttendance[record.studentId] = record.status;
        }
      });
      setAttendance(prev => ({ ...prev, ...existingAttendance }));
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (selectedClass) {
      fetchStudentsByClass();
      fetchAttendanceForDate();
    }
  }, [selectedClass, selectedDate, fetchStudentsByClass, fetchAttendanceForDate]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleSubmit = async () => {
    if (!selectedClass || students.length === 0) {
      toast.error('Please select a class and ensure students are loaded');
      return;
    }

    try {
      setSubmitting(true);
      
      const attendanceData = {
        date: selectedDate,
        className: selectedClass,
        records: students.map(student => ({
          studentId: student._id,
          studentName: student.name,
          rollNumber: student.rollNumber,
          status: attendance[student._id] || 'present'
        }))
      };

      await attendanceService.recordAttendance(attendanceData);
      toast.success('Attendance recorded successfully!');
    } catch (error) {
      toast.error('Failed to record attendance');
      console.error('Error recording attendance:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getAttendanceStats = () => {
    const total = students.length;
    const present = Object.values(attendance).filter(status => status === 'present').length;
    const absent = Object.values(attendance).filter(status => status === 'absent').length;
    const late = Object.values(attendance).filter(status => status === 'late').length;
    
    return { total, present, absent, late };
  };

  const stats = getAttendanceStats();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Attendance Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Record and manage student attendance
          </p>
        </div>

        {/* Controls */}
        <div className="card mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Class
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="input"
              >
                <option value="">Select a class</option>
                {classes.map((className) => (
                  <option key={className} value={className}>
                    {className}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="input"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleSubmit}
                disabled={submitting || !selectedClass || students.length === 0}
                className="btn-primary w-full"
              >
                {submitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  'Save Attendance'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        {selectedClass && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="card text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total Students
              </div>
            </div>
            <div className="card text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.present}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Present
              </div>
            </div>
            <div className="card text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.absent}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Absent
              </div>
            </div>
            <div className="card text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.late}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Late
              </div>
            </div>
          </div>
        )}

        {/* Students List */}
        {selectedClass && (
          <div className="card">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No students found</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No students are enrolled in this class.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Roll Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Attendance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {students.map((student) => (
                      <tr key={student._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {student.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {student.rollNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={attendance[student._id] || 'present'}
                            onChange={(e) => handleAttendanceChange(student._id, e.target.value)}
                            className="input w-32"
                          >
                            <option value="present">Present</option>
                            <option value="absent">Absent</option>
                            <option value="late">Late</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!selectedClass && (
          <div className="card text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Select a class to get started</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Choose a class from the dropdown above to view and record attendance.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;
